import type { ShipFitting } from "@frontier/content/ship-fitting";
import { useCallback, useRef } from "react";
import type { RefObject } from "react";
import type {
  Profile,
  HullId,
  LoadoutId,
  WeaponId,
  ModuleId,
  PassiveId,
} from "@frontier/protocol/contracts";
import type { ContractSelection } from "@frontier/content/contracts";
import type { EquipmentTiers } from "@frontier/content/equipment-tiers";
import type { ProfileSync } from "./profile-sync";
import { api, ApiError, requestId } from "./session-api";

export function useStationActions(
  profile: Profile | null,
  token: RefObject<string>,
  sync: ProfileSync,
  setBusy: (busy: boolean) => void,
  setNotice: (message: string) => void,
  unauthorized: (message: string) => void,
) {
  const purchases = useRef(new Map<string, string>());
  const failed = useCallback(
    (ticket: number, error: Error, fallback: string) => {
      if (ticket !== sync.ticket()) return;
      if (error instanceof ApiError && error.status === 401)
        unauthorized("Сессия истекла. Войди снова или повтори подключение.");
      else setNotice(error.message || fallback);
    },
    [sync, setNotice, unauthorized],
  );
  const changeLoadout = useCallback(
    async (
      loadout: LoadoutId,
      weapon?: WeaponId,
      module?: ModuleId,
      passive?: PassiveId,
      secondaryModule?: ModuleId,
      hull?: HullId,
      tiers?: Partial<EquipmentTiers>,
    ) => {
      if (!profile) return;
      const ticket = sync.ticket();
      setBusy(true);
      try {
        const confirmed = await api.loadout(
            token.current,
            loadout,
            weapon,
            module,
            passive,
            secondaryModule,
            hull ?? profile.hullId,
            tiers ?? profile.equipmentTiers,
          );
        if (sync.commit(confirmed, ticket)) return confirmed;
      } catch (error) {
        failed(
          ticket,
          error instanceof Error
            ? error
            : new Error("Не удалось сменить сборку"),
          "Не удалось сменить сборку",
        );
      } finally {
        if (ticket === sync.ticket()) setBusy(false);
      }
    },
    [profile, token, sync, setBusy, failed],
  );
  const purchaseHull = useCallback(
    async (hullId: HullId) => {
      if (!profile) return;
      const ticket = sync.ticket();
      const identity = `${profile.playerId}:${hullId}`;
      const operation = purchases.current.get(identity) ?? requestId();
      purchases.current.set(identity, operation);
      setBusy(true);
      try {
        if (
          sync.commit(
            await api.purchaseHull(token.current, hullId, operation),
            ticket,
          )
        ) {
          purchases.current.delete(identity);
          setNotice(
            "Корпус добавлен в ангар.",
          );
        }
      } catch (error) {
        if (ticket !== sync.ticket()) return;
        failed(
          ticket,
          error instanceof Error
            ? error
            : new Error("Не удалось приобрести корпус"),
          "Не удалось приобрести корпус",
        );
        const fresh = await sync.refresh(ticket);
        if (ticket === sync.ticket() && fresh?.ownedHulls.includes(hullId))
          purchases.current.delete(identity);
      } finally {
        if (ticket === sync.ticket()) setBusy(false);
      }
    },
    [profile, token, sync, setBusy, setNotice, failed],
  );
  const chooseContracts = useCallback(
    async (contracts: ContractSelection[]) => {
      const ticket = sync.ticket();
      setBusy(true);
      try {
        sync.commit(await api.contracts(token.current, contracts), ticket);
      } catch (error) {
        failed(
          ticket,
          error instanceof Error
            ? error
            : new Error("Не удалось выбрать контракт"),
          "Не удалось выбрать контракт",
        );
      } finally {
        if (ticket === sync.ticket()) setBusy(false);
      }
    },
    [token, sync, setBusy, failed],
  );
  const acceptTrainingContract = useCallback(async () => {
    const ticket = sync.ticket();
    setBusy(true);
    try { sync.commit(await api.trainingContract(token.current), ticket); }
    catch (error) { failed(ticket, error instanceof Error ? error : new Error("Не удалось принять учебный контракт"), "Не удалось принять учебный контракт"); }
    finally { if (ticket === sync.ticket()) setBusy(false); }
  }, [token, sync, setBusy, failed]);
  const installFitting = useCallback(async (fitting: ShipFitting) => {
    if (!profile) return;
    const ticket = sync.ticket(), sessionToken = token.current;
    const operation = requestId();
    setBusy(true);
    try {
      const result = await api.installFitting(sessionToken, fitting, operation);
      if (ticket === sync.ticket() && sessionToken === token.current && sync.commit(result, ticket)) return result;
    } catch (error) {
      failed(ticket, error instanceof Error ? error : new Error("Оснащение не подтверждено"), "Оснащение не подтверждено");
      await sync.refresh(ticket);
    } finally {
      if (ticket === sync.ticket() && sessionToken === token.current) setBusy(false);
    }
  }, [profile, sync, token, setBusy, failed]);
  const selectHull = useCallback(async (hullId: HullId) => {
    const ticket = sync.ticket();
    setBusy(true);
    try { sync.commit(await api.selectHull(token.current, hullId), ticket); }
    catch (error) { failed(ticket, error instanceof Error ? error : new Error("Не удалось выбрать корабль"), "Не удалось выбрать корабль"); }
    finally { if (ticket === sync.ticket()) setBusy(false); }
  }, [sync, token, setBusy, failed]);
  return { changeLoadout, selectHull, installFitting, purchaseHull, chooseContracts, acceptTrainingContract };
}
